import Swiper from 'swiper';
import {Navigation} from 'swiper/modules';
import {MD_WIDTH, LG_WIDTH, XL_WIDTH} from '@main/helpers/consts';
import {debounce} from '@main/helpers/dom.js';

/** Класс слайдера с навигацией и пагинацией. */
export class BaseSlider {
  /**
   * Конструктор класса.
   *
   * @param {HTMLElement} el Элемент.
   */
  constructor(el) {
    this.el = el;
    this.swiperNode = this.el.querySelector('.js-slider');
    this.nextArrow = this.el.querySelector('.js-slider-next');
    this.prevArrow = this.el.querySelector('.js-slider-prev');

    this.init();
  }

  /**
   * Инициализация слайдера и наблюдателей.
   */
  init() {
    this.createSlider();

    // Создаем обсерверы с debounce для оптимизации
    this.createResizeObservers();
  }

  /**
   * Создание наблюдателей за изменением размера.
   */
  createResizeObservers() {
    const resizeObserver = new ResizeObserver(debounce(this.handleResize.bind(this), 200));
    resizeObserver.observe(document.body);
  }

  /**
   * Обработчик изменения размера окна.
   */
  handleResize([entry]) {
    this.onResizeWindow(entry);
  }

  /**
   * Получение конфигурации слайдера.
   */
  getSliderConfig() {
    const {dataset} = this.swiperNode;

    return {
      modules: [Navigation],
      slidesPerView: this.parseNumber(dataset.slidePerView) || 'auto',
      loop: dataset.sliderLoop === 'true',
      speed: 700,
      easing: 'cubic-bezier(0.645, 0.045, 0.355, 1)',
      spaceBetween: this.parseNumber(dataset.slideSpace) || 0,
      touchReleaseOnEdges: true,
      autoHeight: dataset.autoHeight === 'true',
      watchSlidesProgress: true,
      centeredSlides: dataset.centered === 'true',
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      navigation: {
        nextEl: this.nextArrow,
        prevEl: this.prevArrow,
      },
      breakpoints: this.getBreakpointsConfig(),
    };
  }

  /**
   * Получение конфигурации брейкпоинтов.
   */
  getBreakpointsConfig() {
    const {dataset} = this.swiperNode;

    return {
      [MD_WIDTH]: this.getBreakpointConfig('md', dataset),
      [LG_WIDTH]: this.getBreakpointConfig('lg', dataset),
      [XL_WIDTH]: this.getBreakpointConfig('xl', dataset),
    };
  }

  /**
   * Получение конфигурации для конкретного брейкпоинта.
   */
  getBreakpointConfig(prefix, dataset) {
    return {
      autoHeight: dataset.autoHeight === 'true',
      slidesPerView: this.parseNumber(dataset[`${prefix}SlidePerView`]) || 'auto',
      spaceBetween: this.parseNumber(dataset[`${prefix}SlideSpace`]) || 8,
      grid: {
        rows: this.parseNumber(dataset.grid) || 1,
      },
    };
  }

  /**
   * Парсинг числовых значений из data-атрибутов.
   */
  parseNumber(value) {
    return value ? Number(value) : null;
  }

  /**
   * Инициализация слайдера.
   */
  createSlider() {
    if (!this.swiperNode) return;

    this.swiper = new Swiper(this.swiperNode, this.getSliderConfig());
  }

  /**
   * Определение типа устройства по ширине.
   */
  getDeviceType(width) {
    if (width < MD_WIDTH) return 'mobile';
    if (width >= MD_WIDTH && width < LG_WIDTH) return 'tablet';
    return 'desktop';
  }

  /**
   * Получение количества слайдов для текущего устройства.
   */
  getSlidesPerView(deviceType) {
    const {dataset} = this.swiperNode;

    const slidesMap = {
      mobile: dataset.slidePerView,
      tablet: dataset.mdSlidePerView,
      desktop: dataset.lgSlidePerView,
    };

    return slidesMap[deviceType];
  }

  /**
   * Обработчик ресайза окна для условного уничтожения/создания слайдера.
   */
  onResizeWindow(entry) {
    const {dataset} = this.swiperNode;

    if (dataset.lgDestroy !== 'true') return;

    const isDesktop = entry.contentRect.width >= LG_WIDTH;
    const sliderExists = !!this.swiper;

    if (isDesktop && sliderExists) {
      this.swiper.destroy(true, false);
      this.swiper = null;
    } else if (!isDesktop && !sliderExists) {
      this.createSlider();
    }
  }

  /**
   * Уничтожение слайдера.
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, false);
      this.swiper = null;
    }
  }
}

/**
 * Инициализация всех слайдеров на странице.
 */
document.querySelectorAll('.js-slider-block').forEach((slider) => new BaseSlider(slider));
